import React, { useState, useEffect, useContext, useMemo } from "react";
import urlToId from "../../../../helpers/UrlToId";
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Avatar,
  Button,
  ButtonGroup,
  Icon,
  Divider,
  Card,
  CardBody,
  CardHeader,
  Container,
  useToast,
  useDisclosure,
  useColorModeValue,
  IconButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Link as ChakraLink
} from "@chakra-ui/react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  FaTicketAlt,
  FaUser,
  FaEnvelope,
  FaBuilding,
  FaClock,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaComments,
  FaTasks,
  FaHistory,
  FaUpload,
  FaArrowLeft,
  FaEdit,
  FaTrash,
  FaUserPlus,
  FaPlay,
  FaPlus,
  FaVideo
} from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TicketState } from "../../../../components/States";
import client from "../../../../helpers/Client";
import { generateTicketPDFFromTemplate } from '../../../../utils/pdfTemplateGenerator';
import AuthContext from "../../../../contexts/Auth";
import moment from "moment";
import Customer from "./Customer";
import CreateTaskModal from "./modals/CreateTaskModal";
import TaskDetailModal from "./modals/Task";
import AssingTaskModal from "./modals/AssingTaskModal";
import AssignTicketModal from "./modals/AssignTicketModal";
import AssignUnitModal from "./modals/AssignUnitModal";
import Notes from "../../../../components/notes/Notes";
import Logs from "../../../../components/logs/Logs";
import NotesModal from "./modals/NotesModal";
import HistoryModal from "./modals/HistoryModal";
import { SubModalType } from "./Tasks";
import TicketPrintModal from "./modals/TicketPrintModal";

// Leaflet marker icon fix for bundlers (mobile)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const TicketDetailMobile = ({ ticket, ticketID, autoOpenServiceForm, onAction }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useContext(AuthContext);
  const isSuperUser = auth?.data?.user?.is_superuser;
  const [searchParams] = useSearchParams();

  // Extract reseller ID from URL or assigned_to URL (same logic as desktop)
  const getResellerID = () => {
    // First try URL parameter
    const urlResellerID = searchParams.get("bayi");
    if (urlResellerID) return urlResellerID;

    // Then try to extract from assigned_to URL
    if (ticket?.assigned_to?.url) {
      const match = ticket.assigned_to.url.match(/\/resellers\/(\d+)\//);
      if (match) return match[1];
    }

    return null;
  };

  const resellerID = getResellerID();

  const [isLoading, setIsLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    description: true,
    details: false,
    customer: false,
    tasks: false,
    notes: false,
    attachments: false
  });
  const [activeTab, setActiveTab] = useState(0);

  // Modal states
  const createTaskModalState = useDisclosure();
  const taskDetailModalState = useDisclosure();
  const assignTaskModalState = useDisclosure();
  const assignTicketModalState = useDisclosure();
  const assignUnitModalState = useDisclosure();
  const notesModalState = useDisclosure();
  const historyModalState = useDisclosure();
  const ticketPrintModalState = useDisclosure();
  const liveSupportModalState = useDisclosure();

  const [meetingStartDate, setMeetingStartDate] = useState("");
  const [meetingStartTime, setMeetingStartTime] = useState("");
  const [creatingMeeting, setCreatingMeeting] = useState(false);
  const [createdMeeting, setCreatedMeeting] = useState(null);

  useEffect(() => {
    if (!liveSupportModalState.isOpen) return;
    const m = moment().add(30, "minutes").startOf("minute");
    setMeetingStartDate(m.format("YYYY-MM-DD"));
    setMeetingStartTime(m.format("HH:mm"));
    setCreatedMeeting(null);
  }, [liveSupportModalState.isOpen]);

  const suggestedMeetingSlots = useMemo(() => {
    const base = moment().add(30, "minutes");
    const rounded = base.minute(base.minute() < 30 ? 30 : 0).second(0).millisecond(0);
    if (base.minute() >= 30) rounded.add(1, "hour");
    return Array.from({ length: 4 }).map((_, i) => rounded.clone().add(i * 30, "minutes"));
  }, [liveSupportModalState.isOpen]);

  useEffect(() => {
    if (autoOpenServiceForm && ticket && ticket.number !== undefined && ticket.number !== null) {
      ticketPrintModalState.onOpen();
    }
  }, [autoOpenServiceForm, ticket, ticketPrintModalState]);

  const handleCreateLiveSupportMeeting = async () => {
    const dt = moment(`${meetingStartDate} ${meetingStartTime}`, "YYYY-MM-DD HH:mm", true);
    if (!dt.isValid()) {
      toast({ title: "Tarih/saat geçersiz", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    setCreatingMeeting(true);
    try {
      const resp = await client.post(`/tickets/${ticketID}/create_video_meeting/`, { start_time: dt.toISOString() });
      setCreatedMeeting(resp.data || null);
      toast({ title: "Canlı destek randevusu oluşturuldu", status: "success", duration: 3000, isClosable: true });
      if (onAction) onAction("revalidate");
    } catch (e) {
      toast({
        title: "Canlı destek oluşturulamadı",
        description: e?.response?.data?.error || e?.response?.data?.detail || e?.message || "İstek başarısız",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreatingMeeting(false);
    }
  };

  // Task detail state
  const [selectedTaskID, setSelectedTaskID] = useState(null);
  const [activeSubModal, setActiveSubModal] = useState(SubModalType.NONE);
  const [assigningTask, setAssigningTask] = useState(null);

  // Task detail handlers
  const handleTaskOpen = (task) => {
    setSelectedTaskID(urlToId(task.url));
    taskDetailModalState.onOpen();
  };

  const handleTaskClose = () => {
    setSelectedTaskID(null);
    taskDetailModalState.onClose();
  };

  const handleAssign = (task) => {
    setAssigningTask(task);
    assignTaskModalState.onOpen();
  };

  const handleSetType = (task) => {
    // TODO: Implement task type setting for mobile
    toast({
      title: "Görev Tipi",
      description: "Görev tipi ayarlama özelliği yakında eklenecek",
      status: "info",
    });
  };

  const handleSetAppointment = (task) => {
    setActiveSubModal(SubModalType.APPOINTMENT);
  };

  const handleSetService = (task) => {
    setActiveSubModal(SubModalType.SERVICE);
  };

  const handleCreateSubtask = (task) => {
    // TODO: Implement subtask creation for mobile
    toast({
      title: "Alt Görev Oluştur",
      description: "Alt görev oluşturma özelliği yakında eklenecek",
      status: "info",
    });
  };
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const headerBg = useColorModeValue("gray.50", "gray.700");
  const accentColor = "blue.500";
  const subtaskBg = useColorModeValue("blue.50", "blue.900");
  const ticketRevalidator = {
    revalidate: () => {
      if (onAction) {
        onAction('refresh');
      }
    }
  };

  const getStatusConfig = (state) => {
    const configs = {
      new: { color: "blue", icon: FaTicketAlt, text: "Yeni" },
      assigned: { color: "purple", icon: FaUser, text: "Atanmış" },
      in_progress: { color: "orange", icon: FaClock, text: "Devam Ediyor" },
      completed: { color: "green", icon: FaCheckCircle, text: "Tamamlandı" },
      cancelled: { color: "red", icon: FaExclamationTriangle, text: "İptal" }
    };
    return configs[state] || configs.new;
  };



  // Format date for mobile
  const formatMobileDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Handle actions
  const handleAction = async (action, successMessage, callback, extraPayload = {}) => {
    setIsLoading(true);
    try {
      await client.post(`/tickets/${ticketID}/${action}/`, { confirm: true, ...extraPayload });
      toast({
        title: "Başarılı",
        description: successMessage,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      if (callback) callback();
      if (onAction) onAction(action);
    } catch (error) {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "İşlem gerçekleştirilemedi",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmService = async () => {
    if (onAction) onAction("refresh");
  };

  // Handle PDF print
  const handlePrint = async () => {
    try {
      await generateTicketPDFFromTemplate(ticket, getStatusConfig, () => ticket.kind);
      toast({
        title: "Başarılı",
        description: "PDF başarıyla oluşturuldu",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Hata",
        description: "PDF oluşturulurken hata oluştu",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle invoice creation
  const handleCreateInvoice = () => {
    toast({
      title: "Bilgi",
      description: "Fatura oluşturma özelliği yakında eklenecek",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  // Admin-only dealer message builder and copy handler
  const buildDealerMessage = () => {
    const id = ticket?.id || urlToId(ticket?.url);
    const workOrderURL = `https://destek-admin.inohom.cloud/is-emri/${urlToId(ticket?.url)}`;
    const dealerName = ticket?.assigned_to?.name || "-";
    const customerName = ticket?.full_name || "-";
    const customerPhone = ticket?.phone_number || "-";
    return (
      `Sayın Bayimiz,
 
Helpdesk uygulamamızda tarafınıza atanmış ve hâlihazırda bekleyen iş emirleriniz bulunmaktadır. İşlerin güncel durumunu ilerletmeniz gerekmektedir.
 
Ticket ID: ${id}
 
İş Emri URL: ${workOrderURL}
 
Bayi Adı: ${dealerName}
 
Müşteri Adı: ${customerName}
 
Müşteri Telefonu: ${customerPhone}
 
Bilgilerinize sunar, gerekli aksiyonların alınmasını rica ederiz.
 
Saygılarımızla,
inohom Destek Ekibi`);
  };

  const handleCopyDealerMessage = async () => {
    const msg = buildDealerMessage();
    try {
      // Modern Clipboard API (güvenli bağlamlarda çalışır)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(msg);
      } else {
        // Fallback: geçici textarea ile kopyalama
        const textarea = document.createElement("textarea");
        textarea.value = msg;
        textarea.style.position = "fixed";
        textarea.style.top = "-1000px";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(textarea);
        if (!ok) throw new Error("execCommand copy failed");
      }
      toast({
        title: "Kopyalandı",
        description: "Mesaj şablonu panoya kopyalandı",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (e) {
      console.error("Clipboard copy error:", e);
      toast({
        title: "Hata",
        description: "Panoya kopyalama başarısız oldu. Lütfen metni manuel kopyalayın.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle task creation
  const handleCreateTask = () => {
    createTaskModalState.onOpen();
  };

  // Handle scroll to tasks
  const handleScrollToTasks = () => {
    // Görevler tabına geç (index 2)
    setActiveTab(2);
    toast({
      title: "Bilgi",
      description: "Görevler sekmesine yönlendirildi",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleAssignTicket = () => {
    if (ticket.assigned_to.name && !isSuperUser) {
      toast({
        title: "Yetki Hatası",
        description: "Bu işlem için yetkiniz bulunmuyor.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    assignTicketModalState.onOpen();
  };
  const handleAssignUnit = () => {
    if (ticket.related_unit && !isSuperUser) {
      toast({
        title: "Yetki Hatası",
        description: "Bu işlem için yetkiniz bulunmuyor.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }


    assignUnitModalState.onOpen();
  };

  // Handle ticket deletion
  const handleDeleteTicket = () => {
    toast({
      title: "Bilgi",
      description: "Ticket silme özelliği yakında eklenecek",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  const statusConfig = getStatusConfig(ticket.state);

  const MotionContainer = motion(Container);
  const MotionCard = motion(Card);

  return (
    <MotionContainer
      maxW="100%"
      p={0}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Loading Overlay */}
      {isLoading && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.300"
          zIndex={9999}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box
            bg={cardBg}
            p={6}
            borderRadius="xl"
            shadow="xl"
            display="flex"
            alignItems="center"
            gap={4}
          >
            <Spinner size="lg" color="blue.500" />
            <Text fontSize="lg" fontWeight="semibold">
              İşlem gerçekleştiriliyor...
            </Text>
          </Box>
        </Box>
      )}

      <VStack spacing={4} align="stretch" pb={6}>
        {/* Header */}
        <MotionCard
          bg={cardBg}
          border="1px"
          borderColor={borderColor}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <CardHeader pb={2}>

            <Box>  <IconButton
              icon={<FaArrowLeft />}
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              aria-label="Geri dön"
            /> <Badge variant='solid' colorScheme='green' textTransform="uppercase" letterSpacing="wide">
                Ticket #{urlToId(ticket.url)}
              </Badge></Box>







          </CardHeader>
          <CardBody pt={0}>
            <VStack spacing={4} align="stretch">
              {/* Title */}
              <Text
                fontSize="lg"
                fontWeight="bold"
                color={textColor}
                textAlign="center"
                lineHeight="1.3"
              >
                {ticket.title}
              </Text>
              <VStack spacing={2} w="100%">
                <ButtonGroup size="sm" variant="outline" spacing={2} w="100%">
                  {(['in_queue', 'assigned', 'new'].includes(ticket.state)) && (
                    <Button
                      leftIcon={<FaPlay />}
                      colorScheme="green"
                      flex={1}
                      onClick={() => handleAction('start', 'Ticket başlatıldı')}
                    >
                      Başlat
                    </Button>
                  )}
                  {ticket.state === 'in_progress' && (
                    <>
                      <Button
                        leftIcon={<FaCheckCircle />}
                        color="red.600"
                        flex={1}
                        onClick={() => handleAction('complete', 'Ticket tamamlandı')}
                      >
                        Tamamla
                      </Button>
                    </>
                  )}
                  {ticket.state !== 'completed' && ticket.state !== 'cancelled' && (
                    <Button
                      leftIcon={<FaTimesCircle />}
                      colorScheme="red"
                      variant="ghost"
                      size="xs"
                      onClick={() => handleAction('cancel', 'Ticket iptal edildi')}
                    >
                      İptal
                    </Button>
                  )}
                </ButtonGroup>

                {/* Secondary Actions */}
                <ButtonGroup size="xs" variant="outline" spacing={1} w="100%">
                  <Button
                    leftIcon={<FaUserPlus />}
                    onClick={handleAssignTicket}
                    colorScheme="purple"
                    flex={1}
                    fontSize="xs"
                  >
                    Bayi Ata
                  </Button>
                  <Button
                    leftIcon={<FaBuilding />}
                    onClick={handleAssignUnit}
                    colorScheme="orange"
                    flex={1}
                    fontSize="xs"
                  >
                    Unite Ata
                  </Button>
                </ButtonGroup>

                <ButtonGroup size="xs" variant="outline" spacing={1} w="100%">
                  <Button
                    leftIcon={<FaVideo />}
                    onClick={liveSupportModalState.onOpen}
                    colorScheme="teal"
                    flex={1}
                    fontSize="xs"
                  >
                    Canlı Destek
                  </Button>
                  <Button
                    leftIcon={<FaPlus />}
                    onClick={ticketPrintModalState.onOpen}
                    colorScheme="blue"
                    flex={1}
                    fontSize="xs"
                  >
                    Servis Formu
                  </Button>
                  {isSuperUser && (
                    <Button
                      leftIcon={<FaEnvelope />}
                      onClick={handleCopyDealerMessage}
                      colorScheme="blue"
                      flex={1}
                      fontSize="xs"
                    >
                      Mesaj Gönder
                    </Button>
                  )}
                </ButtonGroup>
              </VStack>
            </VStack>
          </CardBody>
        </MotionCard>
        <MotionCard
          bg={cardBg}
          border="1px"
          borderColor={borderColor}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed" size="sm">
            <TabList>
              <Tab flex={1}>
                <HStack spacing={1}>
                  <Icon as={FaComments} boxSize={3} />
                  <Text>Açıklama</Text>
                </HStack>
              </Tab>
              <Tab flex={1}>
                <HStack spacing={1}>
                  <Icon as={FaUser} boxSize={3} />
                  <Text>Müşteri</Text>
                </HStack>
              </Tab>
              <Tab flex={1}>
                <HStack spacing={1}>
                  <Icon as={FaTasks} boxSize={3} />
                  <Text>Görevler</Text>
                </HStack>
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel p={4}>
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color={textColor} mb={2}>
                      Açıklama
                    </Text>
                    <Text
                      fontSize="sm"
                      color={mutedColor}
                      lineHeight="1.5"
                      whiteSpace="pre-wrap"
                    >
                      {ticket.body || "Açıklama bulunmuyor."}
                    </Text>
                  </Box>
                  <Divider />
                  <Box>
                    <HStack spacing={2} justify="space-between">
                      <Text fontSize="sm" fontWeight="600" color={textColor} mb={3}>
                        Teknik Detaylar
                      </Text>
                      <Badge
                        colorScheme={statusConfig.color}
                        size="sm"
                        borderRadius="full"
                        px={3}
                        py={1}
                      >
                        <HStack spacing={1}>
                          <Icon as={statusConfig.icon} boxSize={3} />
                          <Text>{statusConfig.text}</Text>
                        </HStack>
                      </Badge>
                    </HStack>
                    <VStack spacing={2} align="stretch">
                      <HStack justify="space-between">
                        <Text fontSize="xs" color={mutedColor}>Oluşturulma:</Text>
                        <Badge fontSize="xs" color={textColor}>
                          {formatMobileDate(ticket.created_at)}
                        </Badge>
                      </HStack>
                      {ticket.assigned_to && (
                        <HStack justify="space-between">
                          <Text fontSize="xs" color={mutedColor}>Atanan:</Text>
                          <Badge fontSize="xs" color={textColor}>
                            {ticket.assigned_to.name}
                          </Badge>
                        </HStack>
                      )}
                      {ticket.related_unit && (
                        <HStack justify="space-between">
                          <Text fontSize="xs" color={mutedColor}>Birim:</Text>
                          <Badge fontSize="xs" color={textColor}>
                            {ticket.related_unit.name}
                          </Badge>
                        </HStack>
                      )}
                      {ticket.full_name && (
                        <HStack justify="space-between">
                          <Text fontSize="xs" color={mutedColor}>Müşteri:</Text>
                          <Badge fontSize="xs" color={textColor}>
                            {ticket.full_name}
                          </Badge>
                        </HStack>
                      )}
                    </VStack>
                  </Box>
                </VStack>
              </TabPanel>

              <TabPanel p={4}>
                {!!(ticket?.latitude && ticket?.longitude) && (
                  <Box
                    bg={cardBg}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="2xl"
                    overflow="hidden"
                    shadow="xl"
                    mb={4}
                  >
                    <Box px={4} py={3} borderBottom="1px solid" borderColor={borderColor}>
                      <HStack justify="space-between" align="center">
                        <Text fontWeight="bold">Konum</Text>

                        <Button
                          size="sm"

                          onClick={() => {
                            const lat = Number(ticket.latitude);
                            const lng = Number(ticket.longitude);
                            const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
                            window.open(url, "_blank", "noopener,noreferrer");
                          }}
                        >
                          Yol Tarifi
                        </Button>
                      </HStack>
                    </Box>
                    <Box height="220px">
                      <MapContainer
                        center={[Number(ticket.latitude), Number(ticket.longitude)]}
                        zoom={15}
                        style={{ height: "100%", width: "100%" }}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution="&copy; OpenStreetMap contributors"
                        />
                        <Marker position={[Number(ticket.latitude), Number(ticket.longitude)]} />
                      </MapContainer>
                    </Box>
                  </Box>
                )}
                <Customer
                  customerID={ticket.customer ? urlToId(ticket.customer?.url || ticket.customer) : null}
                  ticket={ticket}
                  ticketID={ticketID}
                />
              </TabPanel>

              <TabPanel p={4}>
                <VStack spacing={3} align="stretch">
                  <HStack justify="space-between" align="center">
                    <Text fontSize="sm" fontWeight="600" color={textColor}>
                      Görevler
                    </Text>
                    <Button size="xs" leftIcon={<FaUserPlus />} colorScheme="blue" variant="ghost" onClick={handleCreateTask}>
                      Ekle
                    </Button>
                  </HStack>

                  {/* Tasks List */}
                  {ticket.tasks && ticket.tasks.length > 0 ? (
                    <VStack spacing={3} align="stretch">
                      {ticket.tasks.filter(task => !task.parent_task).map((task, i) => (
                        <Box key={'task-' + i}>
                          {/* Ana Görev */}
                          <Box
                            p={3}
                            border="1px solid"
                            borderColor={borderColor}
                            borderRadius="lg"
                            bg={cardBg}
                          >
                            <VStack spacing={2} align="stretch">
                              <HStack justify="space-between">
                                <Text fontSize="sm" fontWeight="600" color={textColor}>
                                  Alt Görev #{task.number}
                                </Text>
                                <Badge
                                  colorScheme={task.state === 'completed' ? 'green' : task.state === 'in_progress' ? 'blue' : 'gray'}
                                  size="sm"
                                >
                                  {task.state === 'completed' ? 'Tamamlandı' :
                                    task.state === 'in_progress' ? 'Devam Ediyor' :
                                      task.state === 'assigned' ? 'Atandı' : 'Yeni'}
                                </Badge>
                              </HStack>

                              <Text fontSize="xs" color={mutedColor} noOfLines={2}>
                                {task.title}
                              </Text>

                              <HStack spacing={2} flexWrap="wrap">
                                {task.kind && (
                                  <Badge variant="outline" size="xs">
                                    {task.kind}
                                  </Badge>
                                )}
                                {task.assigned_to && (
                                  <Badge variant="outline" size="xs" colorScheme="blue">
                                    {task.assigned_to.name}
                                  </Badge>
                                )}
                                {task.assigned_user && (
                                  <Badge variant="outline" size="xs" colorScheme="green">
                                    {task.assigned_user.full_name || task.assigned_user.username}
                                  </Badge>
                                )}
                              </HStack>

                              <HStack justify="space-between" align="center">
                                <Text fontSize="xs" color={mutedColor}>
                                  {new Date(task.created_at).toLocaleDateString('tr-TR')}
                                </Text>
                                <Button size="xs" variant="ghost" colorScheme="blue" onClick={() => handleTaskOpen(task)}>
                                  Detay
                                </Button>
                              </HStack>
                            </VStack>
                          </Box>

                          {/* Alt Görevler */}
                          {ticket.tasks.filter(subtask => subtask.parent_task && urlToId(subtask.parent_task) === task.id).map((subtask, j) => (
                            <Box
                              key={'subtask-' + j}
                              ml={4}
                              mt={2}
                              p={2}
                              border="1px solid"
                              borderColor={borderColor}
                              borderRadius="md"
                              bg={subtaskBg}
                              borderLeftWidth="3px"
                              borderLeftColor="blue.300"
                            >
                              <VStack spacing={1} align="stretch">
                                <HStack justify="space-between">
                                  <Text fontSize="xs" fontWeight="500" color={textColor}>
                                    ↳ Alt Görev #{subtask.number}
                                  </Text>
                                  <Badge
                                    colorScheme={subtask.state === 'completed' ? 'green' : subtask.state === 'in_progress' ? 'blue' : 'gray'}
                                    size="xs"
                                  >
                                    {subtask.state === 'completed' ? 'Tamamlandı' :
                                      subtask.state === 'in_progress' ? 'Devam Ediyor' :
                                        subtask.state === 'assigned' ? 'Atandı' : 'Yeni'}
                                  </Badge>
                                </HStack>

                                <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                                  {subtask.title}
                                </Text>

                                <HStack justify="space-between" align="center">
                                  <Text fontSize="xs" color={mutedColor}>
                                    {new Date(subtask.created_at).toLocaleDateString('tr-TR')}
                                  </Text>
                                  <Button size="xs" variant="ghost" colorScheme="blue" onClick={() => handleTaskOpen(subtask)}>
                                    Detay
                                  </Button>
                                </HStack>
                              </VStack>
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </VStack>
                  ) : (
                    <Box
                      p={6}
                      textAlign="center"
                      border="2px dashed"
                      borderColor={borderColor}
                      borderRadius="lg"
                    >
                      <Icon as={FaTasks} boxSize={8} color={mutedColor} mb={2} />
                      <Text fontSize="sm" color={mutedColor}>
                        Henüz görev bulunmuyor
                      </Text>
                    </Box>
                  )}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </MotionCard>

        {/* Quick Actions */}
        <MotionCard
          bg={cardBg}
          border="1px"
          borderColor={borderColor}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <CardBody p={4}>
            <Text fontSize="sm" fontWeight="600" color={textColor} mb={3}>
              Hızlı İşlemler
            </Text>
            <VStack spacing={2} align="stretch">
              <Button
                leftIcon={<FaComments />}
                variant="ghost"
                size="sm"
                justifyContent="flex-start"
                onClick={notesModalState.onOpen}
              >
                Not Ekle
              </Button>
              <Button
                leftIcon={<FaUpload />}
                variant="ghost"
                size="sm"
                justifyContent="flex-start"
              >
                Dosya Ekle
              </Button>
              <Button
                leftIcon={<FaHistory />}
                variant="ghost"
                size="sm"
                justifyContent="flex-start"
                onClick={historyModalState.onOpen}
              >
                Geçmişi Görüntüle
              </Button>
            </VStack>
          </CardBody>
        </MotionCard>
      </VStack>

      {/* Modals */}
      <CreateTaskModal
        ticketURL={ticket.url}
        resellerID={resellerID}
        state={createTaskModalState}
        ticketRevalidator={ticketRevalidator}
      />
      <TaskDetailModal
        taskID={selectedTaskID}
        handleClose={handleTaskClose}
        handleAssign={handleAssign}
        handleSetType={handleSetType}
        handleSetAppointment={handleSetAppointment}
        handleSetService={handleSetService}
        handleCreateSubtask={handleCreateSubtask}
        disclosure={taskDetailModalState}
        ticketRendered={0}
        ticketRevalidator={ticketRevalidator}
        activeSubModal={activeSubModal}
        setActiveSubModal={setActiveSubModal}
      />
      <AssingTaskModal
        task={assigningTask}
        resellerID={resellerID}
        ticketRevalidator={ticketRevalidator}
        assignTaskModalState={assignTaskModalState}
      />
      <AssignTicketModal
        ticket={ticket}
        state={assignTicketModalState}
        revalidator={ticketRevalidator}
      />
      <AssignUnitModal
        ticket={ticket}
        state={assignUnitModalState}
        revalidator={ticketRevalidator}
      />
      <NotesModal
        ticket={ticket}
        ticketID={ticketID}
        state={notesModalState}
        revalidator={{ revalidate: () => onAction && onAction('revalidate') }}
      />
      <HistoryModal
        ticketID={ticketID}
        state={historyModalState}
      />
      <Modal isOpen={liveSupportModalState.isOpen} onClose={liveSupportModalState.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Canlı Destek</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color="gray.600">
                Randevu seçince sistem görüşme linkini oluşturur ve müşteriye SMS ile gönderir.
              </Text>

              {ticket?.meeting_link ? (
                <Box border="1px" borderColor="gray.200" borderRadius="md" p={3}>
                  <Text fontWeight="600" mb={1}>
                    Mevcut Görüşme
                  </Text>
                  <ChakraLink href={ticket.meeting_link} isExternal color="blue.600">
                    Linki Aç
                  </ChakraLink>
                </Box>
              ) : null}

              {createdMeeting?.meeting_link || createdMeeting?.support_link ? (
                <Box border="1px" borderColor="green.200" borderRadius="md" p={3} bg="green.50">
                  <Text fontWeight="700" mb={1}>
                    Oluşturulan Link
                  </Text>
                  <ChakraLink href={createdMeeting.support_link || createdMeeting.meeting_link} isExternal color="green.700">
                    Linki Aç
                  </ChakraLink>
                  {createdMeeting?.meeting_link && createdMeeting?.support_link ? (
                    <Box mt={3}>
                      <Text fontSize="sm" fontWeight="600" mb={1}>
                        Müşteri Linki
                      </Text>
                      <ChakraLink href={createdMeeting.meeting_link} isExternal color="green.700">
                        Müşteri Linkini Aç
                      </ChakraLink>
                      <Text fontSize="sm" fontWeight="600" mt={3} mb={1}>
                        Destek Linki
                      </Text>
                      <ChakraLink href={createdMeeting.support_link} isExternal color="green.700">
                        Destek Linkini Aç
                      </ChakraLink>
                    </Box>
                  ) : null}
                  <Text fontSize="xs" color="green.800" mt={1}>
                    SMS gönderildi.
                  </Text>
                </Box>
              ) : null}

              <Box>
                <Text fontSize="sm" fontWeight="600" mb={2}>
                  Önerilen Saatler
                </Text>
                <HStack wrap="wrap" spacing={2}>
                  {suggestedMeetingSlots.map((m) => (
                    <Button
                      key={m.toISOString()}
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setMeetingStartDate(m.format("YYYY-MM-DD"));
                        setMeetingStartTime(m.format("HH:mm"));
                      }}
                    >
                      {m.format("DD.MM")} {m.format("HH:mm")}
                    </Button>
                  ))}
                </HStack>
              </Box>

              <HStack spacing={3}>
                <FormControl>
                  <FormLabel>Tarih</FormLabel>
                  <Input type="date" value={meetingStartDate} onChange={(e) => setMeetingStartDate(e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Saat</FormLabel>
                  <Input type="time" value={meetingStartTime} onChange={(e) => setMeetingStartTime(e.target.value)} />
                </FormControl>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={liveSupportModalState.onClose}>
              Kapat
            </Button>
            <Button colorScheme="teal" onClick={handleCreateLiveSupportMeeting} isLoading={creatingMeeting}>
              Davet Linki Oluştur
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <TicketPrintModal
        isOpen={ticketPrintModalState.isOpen}
        onClose={ticketPrintModalState.onClose}
        ticket={ticket}
        onConfirmService={handleConfirmService}
      />
    </MotionContainer>
  );
};

export default TicketDetailMobile;
